import { QUERIES, WithChildren, WITPaginationState } from '@/_metronic/helpers'
import { BaseResponse } from '@/_metronic/helpers/crud-helper/base-list-response'
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { FilterReportSalesBudget, initialFilter, ReportSalesBudget } from './_models'
import { getReportSalesBudget } from './_requests'
import { useQueryRequestMonthly } from './QueryRequestMonthlyProvider'

/**
 * Begin: Initial Values
 */
export const initialQueryResponseMonthly = {refetch: () => {}, isLoading: false, request: initialFilter}

/**
 * Begin: Response Context
 *
 * It used for context response wrapper, so the response can
 * called everywhere, as long as it wrapped inside the context
 */
type ProductQueryResponseMonthlyContextProps = {
  response?: BaseResponse<ReportSalesBudget> | undefined
  refetch: () => void
  isLoading: boolean
  request: FilterReportSalesBudget
}

function createResponseContext<T>(initialState: ProductQueryResponseMonthlyContextProps) {
  return createContext(initialState)
}

const QueryResponseMonthlyContext = createResponseContext<ReportSalesBudget>(initialQueryResponseMonthly)

/**
 * Begin: The Provider
 */
const QueryResponseMonthlyProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequestMonthly()
  const [request, setRequest] = useState<FilterReportSalesBudget>(state)
  const updatedQuery = useMemo(() => state, [state])

  useEffect(() => {
    if (request !== updatedQuery) {
      setRequest(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.REPORT_SALES_VS_BUDGET}-${JSON.stringify(request)}`,
    () => {
      return getReportSalesBudget(request)
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseMonthlyContext.Provider value={{isLoading: isFetching, refetch, response, request}}>
      {children}
    </QueryResponseMonthlyContext.Provider>
  )
}

const useQueryResponseMonthly = () => useContext(QueryResponseMonthlyContext)

const useQueryResponseMonthlyData = () => {
  const {response} = useQueryResponseMonthly()
  if (!response) {
    return null
  }

  return response?.response?.data || null
}

const useQueryResponseMonthlyPagination = () => {
  const defaultPaginationState: WITPaginationState = {
    page: 1,
    pageSize: 10,
    totalCount: 1,
  }

  const {response} = useQueryResponseMonthly()
  if (!response || !response.response) {
    return defaultPaginationState
  }
  const res = response.response
  return {
    page: res.current_page,
    pageSize: res.limit,
    totalCount: res.total_data,
  } as WITPaginationState
}

const useQueryResponseMonthlyLoading = (): boolean => {
  const {isLoading} = useQueryResponseMonthly()
  return isLoading
}

export {
  QueryResponseMonthlyProvider,
  useQueryResponseMonthly,
  useQueryResponseMonthlyData,
  useQueryResponseMonthlyLoading,
  useQueryResponseMonthlyPagination
}

